import { Avatar, Button, createStyles, makeStyles, Theme, Typography } from "@material-ui/core";
import { useState } from "react";
import { Account } from "mdi-material-ui";
import FormLoadingSpinner from "../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";
import { ILogoutOrProfileSwitchRequiredFormValues } from "../_interfaces/ILogoutOrProfileSwitchRequiredFormValues";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            position: 'relative'
        },
        prompt: {
            marginBottom: theme.spacing(2),
            maxWidth: 800,
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        avatar: {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            width: '75px',
            height: '75px',
            margin: '16px auto'
        },
        accountInformation: {
            margin: theme.spacing(2, 0, 3)
        },
        buttons: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: theme.spacing(4)
        },
        secondaryButton: {
            marginLeft: theme.spacing(4),
            background: theme.palette.darkBackground.light,
            color: theme.palette.darkBackground.contrastText
        }
    }),
);

interface LogoutOrProfileSwitchRequiredFormProps {
    values: ILogoutOrProfileSwitchRequiredFormValues;
    onChange: (patch: Partial<ILogoutOrProfileSwitchRequiredFormValues>) => void;
    onSubmit: (override?: Partial<ILogoutOrProfileSwitchRequiredFormValues>) => void;
    isSubmitting?: boolean;
    error?: string;
    fieldErrors?: Partial<Record<keyof ILogoutOrProfileSwitchRequiredFormValues, string>>;
}

export const LogoutOrProfileSwitchRequiredForm: React.FC<LogoutOrProfileSwitchRequiredFormProps> = ({
    values,
    onChange,
    onSubmit,
    isSubmitting,
    error,
    fieldErrors
}) => {

    /********** State **********/

    const getFormId = () => {
        return `form-logout-or-profile-switch-required-${Date.now()}-${Math.random()}`;
    }

    const [formId] = useState(() => getFormId());

    /********** React hooks **********/

    const classes = useStyles();

    /********** Event handlers **********/

    const onLogoutClicked = (event: React.FormEvent<HTMLFormElement> | React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onSubmit({ logout: true });
    }

    const onSwitchProfilesClicked = (event: React.FormEvent<HTMLFormElement> | React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onSubmit({ switchProfiles: true });
    }

    /********** Render **********/

    return (
        <form onSubmit={values.allowSwitch ? onSwitchProfilesClicked : onLogoutClicked} noValidate autoComplete="off" id={formId} className={classes.form}>
            {values.loading ? <FormLoadingSpinner /> : undefined}
            <Avatar className={classes.avatar}>
                <Account fontSize='large' />
            </Avatar>
            <Typography variant="h4">
                You're currently logged in as:
            </Typography>
            <div className={classes.accountInformation}>
                <Typography variant="h6">
                    {values.currentUserName}
                </Typography>
                <Typography variant="h6">
                    {values.currentUserEmail}
                </Typography>
            </div>
            <Typography variant="h6" className={classes.prompt}>
                {values.prompt}
            </Typography>
            {values.allowSwitch
                ? <div className={classes.buttons}>
                    <Button color="primary" variant="contained" type="submit" form={formId} size="large" disabled={isSubmitting}>
                        Switch Profiles
                    </Button>
                    <Button color="primary" variant="contained" onClick={onLogoutClicked} size="large" disabled={isSubmitting} className={classes.secondaryButton}>
                        Logout
                    </Button>
                </div>
                : <Button color="primary" variant="contained" type="submit" form={formId} size="large" disabled={isSubmitting}>
                    Logout
                </Button>
            }
        </form>
    );
};